import {
  AppBar,
  Box,
  alpha,
  Container,
  Icon,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ArrowBack } from "@mui/icons-material";
import Profile from "./profile";
import { cloneElement, JSX, ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";
import { useTemplate } from "../../context/templateContext";

interface TopBarProps {
  isPublic?: Boolean;
  setOpenDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenRightBar?: React.Dispatch<React.SetStateAction<boolean>>;
  openRightBar?: boolean;
  iconLogo: React.ReactElement<{ style?: React.CSSProperties }>;
  link?: string;
  tools?: ReactElement;
  socket?: Socket | null;
  colorMode?: boolean;
  setColorMode?: React.Dispatch<React.SetStateAction<boolean>>;
  ocultar?: boolean;
  backgroundColor?: string | null;
}

export default function TopBar({
  isPublic = false,
  setOpenDrawer = () => {},
  setOpenRightBar = () => {},
  iconLogo,
  link = "/",
  tools,
  socket = null,
  colorMode = true,
  setColorMode = () => {},
  ocultar = false,
  backgroundColor = null,
}: TopBarProps): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const [administrador, setAdministrador] = useState<boolean>(false);

  const { onBack, showBackButton, pageTitle } = useTemplate();

  useEffect(() => {
    const sidebarOpen = JSON.parse(
      localStorage.getItem("sidebarCollapsed") || "false"
    );
    setOpenRightBar(sidebarOpen);
  }, [setOpenRightBar]);

  const styles = {
    deleteButton: {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.error.main
          : theme.palette.error.dark,
      borderColor:
        theme.palette.mode === "dark"
          ? theme.palette.error.light
          : theme.palette.error.dark,
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.error.light, 0.2)
            : alpha(theme.palette.error.light, 0.2),
      },
    },
    button: {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.primary.light
          : theme.palette.primary.dark,
      borderColor:
        theme.palette.mode === "dark"
          ? theme.palette.primary.light
          : theme.palette.primary.dark,
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.light, 0.2)
            : alpha(theme.palette.primary.light, 0.2),
      },
    },
    appBar: {
      minWidth: "350px",
      width: "100%",
      position: "static",
      height: "65px",
      backgroundColor: alpha(theme.palette.background.default, 0.9),
      backDropFilter: "blur(6px)",
      zIndex: 1000,
    },
    container: { padding: 0, "&.MuiContainer-root": { paddingX: 0 } },
    toolbar: {
      width: "100%",
      padding: 0,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      paddingX: 1,
    },
    boxMenuAndLogo: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: 1,
      width: "auto",
      flex: 1,
    },
    iconButtonLogo: {
      flexDirection: "row",
      alignItems: "center",
      marginX: !ocultar ? 1 : 6,
      padding: 0,
      height: "40px",
      borderRadius: 1,
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    icon: {
      width: "180px",
      height: "50px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
    imgIcon: {
      width: 170,
      height: "auto",
    },
    buttonMenuDawer: {
      padding: 1,
      display: { xs: "flex", lg: "none" },
    },
    buttonMenuRightBar: {
      padding: 1,
      display: { xs: "none", lg: "flex" },
    },
    boxRight: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    buttonsContainer: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      marginRight: 2,
    },
    pageTitle: {
      marginLeft: 2,
      flex: 1,
      textAlign: "Start",
      color: theme.palette.text.primary,
    },
  };

  const handleDrawerToggle = (): void => {
    setOpenDrawer((prevValue) => !prevValue);
  };

  const customIconLogo = cloneElement(iconLogo as React.ReactElement<any>, {
    style: {
      ...(iconLogo.props.style || {}),
      width: "180px",
      fill: theme.palette.text.primary,
    },
    role: "img",
  });

  useEffect(() => {
    const handleUsuarioId = (): void => {
      const result = localStorage.getItem("1");
      if (result) {
        const parsedResult = JSON.parse(result);
        if (
          parsedResult.usuarioCliente &&
          (parsedResult.usuarioCliente.codigoPermissao === "adm001" ||
            parsedResult.usuarioCliente.codigoPermissao === "adm002")
        ) {
          setAdministrador(true);
        }
      }
    };
    handleUsuarioId();
  }, []);

  return (
    <AppBar
      position="sticky"
      sx={styles.appBar}
      elevation={0}
      variant="outlined"
    >
      <Container
        maxWidth={false}
        sx={{ ...styles.container, maxWidth: "100vw" }}
      >
        <Toolbar sx={styles.toolbar} disableGutters>
          <Box sx={styles.boxMenuAndLogo}>
            {!isPublic && showBackButton && (
              <IconButton
                onClick={onBack ? onBack : () => {}}
                sx={{ padding: 1 }}
              >
                <ArrowBack />
              </IconButton>
            )}

            {!ocultar && (
              <>
                <IconButton
                  sx={styles.buttonMenuDawer}
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              </>
            )}

            {isPublic && (
              <IconButton
                onClick={() => navigate(link)}
                sx={styles.iconButtonLogo}
              >
                <Icon sx={styles.icon}>{customIconLogo}</Icon>
              </IconButton>
            )}

            {!isPublic && pageTitle && (
              <Typography variant="h6" sx={styles.pageTitle}>
                {pageTitle}
              </Typography>
            )}
          </Box>

          <Box sx={styles.boxRight}>
            {tools}
            {/* <Notifications
              socket={socket}
              snackbar={snackbar}
              setSnackbar={setSnackbar}
              //style={{ display: { xs: "flex", xsm: "none" } }}
            /> */}
            {!isPublic && (
              <Profile
                socket={socket}
                colorMode={colorMode}
                setColorMode={setColorMode}
                administrador={administrador}
              />
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
